import { useState } from 'react';
import { T, useGT } from 'gt-react';
import { toast } from 'src/components/ui/toast';
import { cn } from '@/lib/utils';
import { NoteComposer } from '@/components/common/comments/NoteComposer';
import { Button } from '@/components/ui/button';
import type { TaskReviewLoadedProps } from '@/pages/authenticated/tasks/$taskId/TaskReviewLoadedProps';
import {
  buildArTaskFeed,
  filterArTaskFeed,
  type ArTaskFeedFilter,
  type ArTaskFeedSources,
} from './arTaskFeed';
import { ArTaskFeedRow } from './ArTaskFeedRows';
import { useArTaskNotes } from './useArTaskNotes';

const FILTERS: readonly ArTaskFeedFilter[] = ['all', 'notes', 'erp'];

function FilterChips({
  value,
  onChange,
}: {
  value: ArTaskFeedFilter;
  onChange: (filter: ArTaskFeedFilter) => void;
}) {
  const gt = useGT();
  const context = 'AR task feed, filter chip';
  const labels: Record<ArTaskFeedFilter, string> = {
    all: gt('All', { context }),
    notes: gt('Notes', { context }),
    erp: gt('ERP', { context }),
  };
  return (
    <div className="flex gap-1">
      {FILTERS.map((filter) => (
        <button
          key={filter}
          type="button"
          aria-pressed={filter === value}
          onClick={() => onChange(filter)}
          className={cn(
            'rounded-full border px-2 py-0.5 text-[11px] font-medium',
            filter === value
              ? 'border-stone-800 bg-stone-800 text-white'
              : 'text-stone-600 hover:bg-stone-100',
          )}
        >
          {labels[filter]}
        </button>
      ))}
    </div>
  );
}

/** Posts a task note; toasts its outcome and keeps a failed draft. */
function useSubmitNote(create: (body: string) => Promise<unknown>) {
  const gt = useGT();
  return async (body: string): Promise<boolean> => {
    try {
      await create(body);
      toast.success(
        gt('Note added', {
          context: 'toast after posting a note on an AR task',
        }),
      );
      return true;
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : gt('Failed to add note', {
              context: 'toast after posting a note on an AR task failed',
            }),
      );
      return false;
    }
  };
}

function FeedEmpty({ isLoading }: { isLoading: boolean }) {
  return (
    <p className="m-0 px-0.5 py-1 text-[11px] text-muted-foreground">
      {isLoading ? (
        <T context="AR task feed, loading">Loading…</T>
      ) : (
        <T context="AR task feed, nothing to show for the chosen filter">
          Nothing here yet.
        </T>
      )}
    </p>
  );
}

/**
 * One newest-first feed of the task's notes, the record's ERP notes and
 * events, and the task's key events, with the note composer on top. The
 * task's own notes keep edit and delete for their author.
 */
export function ArTaskNotesFeed({
  loaded,
  sources,
}: {
  loaded: TaskReviewLoadedProps;
  sources: Omit<ArTaskFeedSources, 'comments' | 'timeline'>;
}) {
  const [filter, setFilter] = useState<ArTaskFeedFilter>('all');
  const notes = useArTaskNotes(loaded.task.id);
  const submit = useSubmitNote((body) => notes.create.mutateAsync(body));
  const entries = filterArTaskFeed(
    buildArTaskFeed({
      ...sources,
      comments: notes.comments,
      timeline: loaded.timeline.entries,
    }),
    filter,
  );
  const people = {
    currentUserId: loaded.currentUserId,
    userNameById: loaded.userNameById,
    onUpdate: notes.onUpdate,
    onDelete: notes.onDelete,
  };
  return (
    <section className="flex min-w-0 flex-col gap-2.5">
      <div className="flex items-center justify-between gap-2">
        <h3 className="m-0 text-[11px] font-semibold tracking-[0.06em] text-stone-500 uppercase">
          <T context="AR task detail, heading over the task's notes and activity feed">
            Notes and activity
          </T>
        </h3>
        <FilterChips value={filter} onChange={setFilter} />
      </div>
      <NoteComposer onSubmit={submit} isSubmitting={notes.create.isPending} />
      {entries.length === 0 ? (
        <FeedEmpty isLoading={notes.isLoading} />
      ) : (
        <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
          {entries.map((entry) => (
            <ArTaskFeedRow key={entry.id} entry={entry} people={people} />
          ))}
        </ul>
      )}
      <LoadOlderTaskEvents timeline={loaded.timeline} />
    </section>
  );
}

/**
 * The task's timeline loads in pages, so its older status and assignee
 * changes come in on demand.
 */
function LoadOlderTaskEvents({
  timeline,
}: {
  timeline: TaskReviewLoadedProps['timeline'];
}) {
  if (!timeline.hasMore) return null;
  return (
    <Button
      variant="ghost"
      size="sm"
      className="self-center text-xs"
      disabled={timeline.isFetchingMore}
      onClick={timeline.loadMore}
    >
      {timeline.isFetchingMore ? (
        <T context="AR task feed, older task events loading">Loading…</T>
      ) : (
        <T context="AR task feed, button that loads older task events">
          Load older task events
        </T>
      )}
    </Button>
  );
}
